import React, { useState, useEffect, useRef } from 'react';

// ============================================================================
// Getting Started with x402 on Stacks
// A fun, engaging single-page guide for Bitcoin micropayments
// ============================================================================

// Code examples as constants for easy maintenance
const SERVER_CODE = `import { x402PaymentRequired } from 'x402-stacks';
import express from 'express';

const app = express();

app.get('/api/joke',
  x402PaymentRequired({
    amount: 1000,  // 1000 microSTX (~$0.001)
    recipient: 'SP2...'  // Your Stacks address
  }),
  (req, res) => {
    res.json({
      joke: "Why do Bitcoin maxis hate the beach? " +
            "Too much sand (Satoshi And Nakamoto Deniers)."
    });
  }
);

app.listen(3000);`;

const CLIENT_CODE = `import { x402Client } from 'x402-stacks';

const client = x402Client({
  privateKey: process.env.STX_PRIVATE_KEY
});

const response = await client.fetch(
  'https://api.example.com/api/joke'
);

console.log(response.data.joke);
// Payment handled automatically when server returns 402`;

const CURL_EXAMPLES = [
  {
    name: "Random Dad Joke",
    endpoint: "https://api.stx402.com/joke",
    description: "Get a random programming joke",
    price: "1,000 μSTX"
  },
  {
    name: "Random Number",
    endpoint: "https://api.stx402.com/random",
    description: "Cryptographically random number",
    price: "500 μSTX"
  },
  {
    name: "AI Prompt",
    endpoint: "https://api.stx402.com/ai/complete",
    description: "Quick AI text completion",
    price: "10,000 μSTX"
  }
];

// ============================================================================
// Components
// ============================================================================

// Copy button with feedback
const CopyButton: React.FC<{ text: string }> = ({ text }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button
      onClick={handleCopy}
      className="absolute top-3 right-3 px-3 py-1.5 rounded-md text-xs font-medium
                 bg-gray-700 hover:bg-gray-600 text-gray-300 hover:text-white
                 transition-all duration-200 flex items-center gap-1.5"
    >
      {copied ? (
        <>
          <CheckIcon /> Copied!
        </>
      ) : (
        <>
          <CopyIcon /> Copy
        </>
      )}
    </button>
  );
};

// Simple icons
const CopyIcon = () => (
  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
          d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
  </svg>
);

const CheckIcon = () => (
  <svg className="w-3.5 h-3.5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
);

const ArrowRightIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
  </svg>
);

// Code block with syntax highlighting (simplified)
const CodeBlock: React.FC<{ code: string; language?: string }> = ({ code, language = 'typescript' }) => {
  // Simple syntax highlighting for TypeScript/JavaScript
  const highlightCode = (code: string) => {
    return code
      // Strings
      .replace(/(["'`])(?:(?!\1)[^\\]|\\.)*\1/g, '<span class="text-amber-300">$&</span>')
      // Keywords
      .replace(/\b(import|export|from|const|let|var|function|async|await|return|if|else|new)\b/g,
               '<span class="text-purple-400">$1</span>')
      // Comments
      .replace(/(\/\/.*$)/gm, '<span class="text-gray-500">$1</span>')
      // Numbers
      .replace(/\b(\d+)\b/g, '<span class="text-cyan-300">$1</span>')
      // Function calls
      .replace(/(\w+)(\s*\()/g, '<span class="text-blue-300">$1</span>$2')
      // Object keys
      .replace(/(\w+):/g, '<span class="text-orange-300">$1</span>:');
  };

  return (
    <div className="relative group">
      <div className="absolute -inset-0.5 bg-gradient-to-r from-orange-500/20 to-amber-500/20
                      rounded-lg blur opacity-30 group-hover:opacity-50 transition duration-300" />
      <div className="relative bg-gray-900 rounded-lg border border-gray-800 overflow-hidden">
        <div className="flex items-center gap-2 px-4 py-2 bg-gray-800/50 border-b border-gray-800">
          <div className="flex gap-1.5">
            <div className="w-3 h-3 rounded-full bg-red-500/80" />
            <div className="w-3 h-3 rounded-full bg-yellow-500/80" />
            <div className="w-3 h-3 rounded-full bg-green-500/80" />
          </div>
          <span className="text-xs text-gray-500 ml-2">{language}</span>
        </div>
        <pre className="p-4 overflow-x-auto text-sm leading-relaxed">
          <code
            className="text-gray-300"
            dangerouslySetInnerHTML={{ __html: highlightCode(code) }}
          />
        </pre>
        <CopyButton text={code} />
      </div>
    </div>
  );
};

// Animated section wrapper
const Section: React.FC<{
  children: React.ReactNode;
  className?: string;
  id?: string;
}> = ({ children, className = '', id }) => {
  const ref = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={ref}
      id={id}
      className={`transition-all duration-700 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      } ${className}`}
    >
      {children}
    </section>
  );
};

// Feature card component
const FeatureCard: React.FC<{
  icon: string;
  title: string;
  description: string;
}> = ({ icon, title, description }) => (
  <div className="group relative">
    <div className="absolute -inset-0.5 bg-gradient-to-r from-orange-500/30 to-amber-500/30
                    rounded-xl blur opacity-0 group-hover:opacity-100 transition duration-300" />
    <div className="relative bg-gray-900/80 backdrop-blur border border-gray-800 rounded-xl p-6
                    hover:border-orange-500/50 transition-all duration-300">
      <div className="text-3xl mb-3">{icon}</div>
      <h3 className="text-lg font-bold text-white mb-2">{title}</h3>
      <p className="text-gray-400 text-sm">{description}</p>
    </div>
  </div>
);

// Flow diagram step
const FlowStep: React.FC<{
  number: number;
  title: string;
  description: string;
  isLast?: boolean;
}> = ({ number, title, description, isLast = false }) => (
  <div className="flex items-start gap-4">
    <div className="flex flex-col items-center">
      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-500 to-amber-600
                      flex items-center justify-center text-white font-bold text-lg shadow-lg
                      shadow-orange-500/20">
        {number}
      </div>
      {!isLast && (
        <div className="w-0.5 h-16 bg-gradient-to-b from-orange-500/50 to-transparent mt-2" />
      )}
    </div>
    <div className="pt-1">
      <h4 className="font-semibold text-white text-lg">{title}</h4>
      <p className="text-gray-400 mt-1">{description}</p>
    </div>
  </div>
);

// Link card component
const LinkCard: React.FC<{
  href: string;
  title: string;
  description: string;
  icon: string;
}> = ({ href, title, description, icon }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="group block"
  >
    <div className="relative overflow-hidden bg-gray-900/80 backdrop-blur border border-gray-800
                    rounded-xl p-6 hover:border-orange-500/50 transition-all duration-300
                    hover:shadow-lg hover:shadow-orange-500/10">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-2xl mb-2">{icon}</div>
          <h3 className="font-bold text-white group-hover:text-orange-400 transition-colors">
            {title}
          </h3>
          <p className="text-gray-400 text-sm mt-1">{description}</p>
        </div>
        <div className="text-gray-600 group-hover:text-orange-400 group-hover:translate-x-1
                        transition-all duration-300">
          <ArrowRightIcon />
        </div>
      </div>
    </div>
  </a>
);

// ============================================================================
// Main Component
// ============================================================================

const GettingStarted: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-950 text-gray-100">
      {/* Background gradient effects */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-orange-500/10 rounded-full blur-3xl" />
        <div className="absolute top-1/3 -left-40 w-80 h-80 bg-amber-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-orange-600/5 rounded-full blur-3xl" />
      </div>

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-gray-950/80 backdrop-blur-lg border-b border-gray-800/50">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-2xl">⚡</span>
            <span className="font-bold text-xl">
              <span className="text-orange-400">x402</span>
              <span className="text-gray-400"> on Stacks</span>
            </span>
          </div>
          <div className="hidden md:flex items-center gap-6 text-sm">
            <a href="#quickstart" className="text-gray-400 hover:text-white transition-colors">Quick Start</a>
            <a href="#examples" className="text-gray-400 hover:text-white transition-colors">Examples</a>
            <a href="https://stx402.com" target="_blank" rel="noopener noreferrer"
               className="text-gray-400 hover:text-white transition-colors">Registry</a>
            <a href="https://github.com/x402-stacks" target="_blank" rel="noopener noreferrer"
               className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg
                          font-medium transition-colors">
              GitHub
            </a>
          </div>
        </div>
      </nav>

      <main className="relative max-w-6xl mx-auto px-6 pt-24 pb-20">

        {/* ================================================================ */}
        {/* Hero Section */}
        {/* ================================================================ */}
        <Section className="pt-16 pb-20 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-500/10 border border-orange-500/20
                          rounded-full text-orange-400 text-sm font-medium mb-8">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-orange-500" />
            </span>
            HTTP 402 is finally here
          </div>

          <h1 className="text-5xl md:text-7xl font-black mb-6 leading-tight">
            Give Your API a{' '}
            <span className="bg-gradient-to-r from-orange-400 via-amber-400 to-orange-500
                             bg-clip-text text-transparent">
              Bitcoin Wallet
            </span>
          </h1>

          <p className="text-xl text-gray-400 max-w-2xl mx-auto mb-10">
            Accept micropayments for any endpoint. No accounts, no API keys, no subscriptions.
            <br />
            <span className="text-orange-400">Just pay-per-request on Stacks.</span>
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="#server"
               className="group px-8 py-4 bg-gradient-to-r from-orange-500 to-amber-500
                          text-white font-bold rounded-xl hover:shadow-lg hover:shadow-orange-500/25
                          transition-all duration-300 flex items-center justify-center gap-2">
              Build a Server
              <span className="group-hover:translate-x-1 transition-transform">→</span>
            </a>
            <a href="#client"
               className="px-8 py-4 bg-gray-800 hover:bg-gray-700 text-white font-bold rounded-xl
                          border border-gray-700 hover:border-gray-600 transition-all duration-300
                          flex items-center justify-center gap-2">
              Make a Payment
            </a>
          </div>

          {/* Quick stats */}
          <div className="flex flex-wrap justify-center gap-8 mt-16 text-center">
            <div>
              <div className="text-3xl font-bold text-white">~$0.001</div>
              <div className="text-sm text-gray-500">per request</div>
            </div>
            <div className="w-px bg-gray-800" />
            <div>
              <div className="text-3xl font-bold text-white">&lt;2s</div>
              <div className="text-sm text-gray-500">finality</div>
            </div>
            <div className="w-px bg-gray-800" />
            <div>
              <div className="text-3xl font-bold text-white">0</div>
              <div className="text-sm text-gray-500">accounts needed</div>
            </div>
          </div>
        </Section>

        {/* ================================================================ */}
        {/* How It Works - Flow Diagram */}
        {/* ================================================================ */}
        <Section className="py-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4">What Just Happened? 🤔</h2>
            <p className="text-gray-400">Three steps. That's it.</p>
          </div>

          <div className="max-w-xl mx-auto">
            <FlowStep
              number={1}
              title="Request hits a paywall"
              description="Client requests your API. Server returns HTTP 402 with payment details (amount, address, network)."
            />
            <FlowStep
              number={2}
              title="Client pays automatically"
              description="x402 client signs & broadcasts STX payment. Transaction confirms in ~2 seconds."
            />
            <FlowStep
              number={3}
              title="Server delivers the goods"
              description="Payment verified on-chain. Server returns the actual response. Done."
              isLast
            />
          </div>

          {/* Visual flow (horizontal on desktop) */}
          <div className="hidden lg:flex items-center justify-center gap-4 mt-16 px-8">
            <div className="flex-1 max-w-xs bg-gray-900 border border-gray-800 rounded-xl p-4 text-center">
              <div className="text-2xl mb-2">📱</div>
              <div className="font-medium text-white">Client</div>
              <div className="text-xs text-gray-500 mt-1">GET /api/joke</div>
            </div>
            <div className="text-orange-500 animate-pulse">→</div>
            <div className="flex-1 max-w-xs bg-gray-900 border border-orange-500/30 rounded-xl p-4 text-center">
              <div className="text-2xl mb-2">💰</div>
              <div className="font-medium text-orange-400">402 Payment Required</div>
              <div className="text-xs text-gray-500 mt-1">amount: 1000 μSTX</div>
            </div>
            <div className="text-orange-500 animate-pulse">→</div>
            <div className="flex-1 max-w-xs bg-gray-900 border border-gray-800 rounded-xl p-4 text-center">
              <div className="text-2xl mb-2">⚡</div>
              <div className="font-medium text-white">STX Transfer</div>
              <div className="text-xs text-gray-500 mt-1">signed & broadcast</div>
            </div>
            <div className="text-orange-500 animate-pulse">→</div>
            <div className="flex-1 max-w-xs bg-gray-900 border border-green-500/30 rounded-xl p-4 text-center">
              <div className="text-2xl mb-2">✅</div>
              <div className="font-medium text-green-400">200 OK</div>
              <div className="text-xs text-gray-500 mt-1">{"{ joke: '...' }"}</div>
            </div>
          </div>
        </Section>

        {/* ================================================================ */}
        {/* Server Quick Start */}
        {/* ================================================================ */}
        <Section id="server" className="py-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-purple-500/10 border border-purple-500/20
                              rounded-full text-purple-400 text-xs font-medium mb-4">
                SERVER SIDE
              </div>
              <h2 className="text-4xl font-bold text-white mb-4">
                Accept Payments in <span className="text-orange-400">One Line</span>
              </h2>
              <p className="text-gray-400 mb-6">
                Wrap any Express endpoint with our middleware. Set your price, paste your Stacks address.
                <span className="text-white font-medium"> That's literally it.</span>
              </p>
              <div className="flex items-center gap-4 text-sm">
                <div className="flex items-center gap-2 text-green-400">
                  <span>✓</span> Non-custodial
                </div>
                <div className="flex items-center gap-2 text-green-400">
                  <span>✓</span> Instant settlement
                </div>
                <div className="flex items-center gap-2 text-green-400">
                  <span>✓</span> Any Express app
                </div>
              </div>
            </div>
            <div id="quickstart">
              <CodeBlock code={SERVER_CODE} />
              <p className="text-gray-500 text-sm mt-4 text-center">
                One middleware. Done. 🎉
              </p>
            </div>
          </div>
        </Section>

        {/* ================================================================ */}
        {/* Client Quick Start */}
        {/* ================================================================ */}
        <Section id="client" className="py-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <CodeBlock code={CLIENT_CODE} />
              <p className="text-gray-500 text-sm mt-4 text-center">
                402 responses handled automatically. Request → Pay → Get Data.
              </p>
            </div>
            <div className="order-1 lg:order-2">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-blue-500/10 border border-blue-500/20
                              rounded-full text-blue-400 text-xs font-medium mb-4">
                CLIENT SIDE
              </div>
              <h2 className="text-4xl font-bold text-white mb-4">
                Pay for APIs <span className="text-orange-400">Automatically</span>
              </h2>
              <p className="text-gray-400 mb-6">
                The x402 client intercepts 402 responses and handles payment.
                Your code just looks like a normal fetch call.
              </p>
              <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-4 mt-6">
                <div className="flex items-start gap-3">
                  <div className="text-2xl">💡</div>
                  <div>
                    <div className="font-medium text-white text-sm">Pro tip</div>
                    <div className="text-gray-400 text-sm mt-1">
                      Set spending limits per-domain or per-request. Never worry about runaway costs.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Section>

        {/* ================================================================ */}
        {/* Why Stacks */}
        {/* ================================================================ */}
        <Section className="py-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4">Why Stacks? ⚡</h2>
            <p className="text-gray-400">Bitcoin security meets smart contract flexibility.</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <FeatureCard
              icon="₿"
              title="Bitcoin Finality"
              description="Every transaction anchors to Bitcoin. The most secure settlement layer in existence."
            />
            <FeatureCard
              icon="⚡"
              title="Fast & Cheap"
              description="~2 second blocks. Fraction-of-a-cent fees. Perfect for micropayments."
            />
            <FeatureCard
              icon="🔒"
              title="Clarity Contracts"
              description="Decidable smart contracts. No reentrancy bugs. What you see is what you get."
            />
            <FeatureCard
              icon="🏦"
              title="Non-Custodial"
              description="Payments go directly to your wallet. No middlemen, no platform risk."
            />
          </div>
        </Section>

        {/* ================================================================ */}
        {/* Real Examples */}
        {/* ================================================================ */}
        <Section id="examples" className="py-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4">Try It Now 🚀</h2>
            <p className="text-gray-400">Live endpoints you can test right now. Paste into your terminal.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {CURL_EXAMPLES.map((example, index) => (
              <div key={index} className="bg-gray-900/80 border border-gray-800 rounded-xl overflow-hidden">
                <div className="p-6">
                  <h3 className="font-bold text-white text-lg">{example.name}</h3>
                  <p className="text-gray-400 text-sm mt-1">{example.description}</p>
                  <div className="inline-flex items-center gap-1.5 mt-3 px-2 py-1 bg-orange-500/10
                                  border border-orange-500/20 rounded text-orange-400 text-xs font-mono">
                    {example.price}
                  </div>
                </div>
                <div className="bg-gray-950 border-t border-gray-800 p-4">
                  <div className="relative">
                    <code className="text-xs text-gray-400 break-all">
                      curl -X GET "{example.endpoint}"
                    </code>
                    <CopyButton text={`curl -X GET "${example.endpoint}"`} />
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-8">
            <p className="text-gray-500 text-sm">
              Note: You'll need an x402-compatible client to complete payment.
              <a href="https://stx402.com/docs/client" className="text-orange-400 hover:underline ml-1">
                Get the client →
              </a>
            </p>
          </div>
        </Section>

        {/* ================================================================ */}
        {/* Explore More */}
        {/* ================================================================ */}
        <Section className="py-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4">Explore More 🔍</h2>
            <p className="text-gray-400">Everything you need to build and explore.</p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            <LinkCard
              href="https://stx402.com"
              icon="📡"
              title="Endpoint Registry"
              description="Browse & register x402-enabled endpoints"
            />
            <LinkCard
              href="https://scan.stacksx402.com"
              icon="🔎"
              title="Transaction Scanner"
              description="View live x402 transactions on Stacks"
            />
            <LinkCard
              href="https://github.com/x402-stacks"
              icon="💻"
              title="GitHub"
              description="Read the source, file issues, contribute"
            />
            <LinkCard
              href="https://aibtc.com"
              icon="🤝"
              title="Community"
              description="Join the aibtc ecosystem"
            />
          </div>
        </Section>

        {/* ================================================================ */}
        {/* CTA Section */}
        {/* ================================================================ */}
        <Section className="py-20">
          <div className="relative overflow-hidden bg-gradient-to-br from-orange-500/10 to-amber-500/10
                          border border-orange-500/20 rounded-2xl p-12 text-center">
            <div className="absolute top-0 right-0 w-64 h-64 bg-orange-500/10 rounded-full blur-3xl" />
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-amber-500/10 rounded-full blur-3xl" />

            <div className="relative">
              <h2 className="text-4xl font-bold text-white mb-4">
                Ready to monetize your API?
              </h2>
              <p className="text-gray-400 max-w-xl mx-auto mb-8">
                Start accepting Bitcoin-secured micropayments in minutes.
                No signup required.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href="https://stx402.com/register"
                   className="px-8 py-4 bg-gradient-to-r from-orange-500 to-amber-500
                              text-white font-bold rounded-xl hover:shadow-lg hover:shadow-orange-500/25
                              transition-all duration-300">
                  Register Your Endpoint
                </a>
                <a href="https://github.com/x402-stacks"
                   className="px-8 py-4 bg-gray-800 hover:bg-gray-700 text-white font-bold rounded-xl
                              border border-gray-700 hover:border-gray-600 transition-all duration-300">
                  View on GitHub
                </a>
              </div>
            </div>
          </div>
        </Section>

      </main>

      {/* ================================================================ */}
      {/* Footer */}
      {/* ================================================================ */}
      <footer className="border-t border-gray-800/50 bg-gray-950/80 backdrop-blur">
        <div className="max-w-6xl mx-auto px-6 py-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2">
              <span className="text-xl">⚡</span>
              <span className="text-gray-400">
                Built with <span className="text-orange-400">₿</span> by the x402 Stacks community
              </span>
            </div>
            <div className="flex items-center gap-6">
              <a href="https://discord.gg/x402stacks"
                 className="text-gray-400 hover:text-white transition-colors"
                 target="_blank" rel="noopener noreferrer">
                Discord
              </a>
              <a href="https://twitter.com/x402stacks"
                 className="text-gray-400 hover:text-white transition-colors"
                 target="_blank" rel="noopener noreferrer">
                X / Twitter
              </a>
              <a href="https://github.com/x402-stacks"
                 className="text-gray-400 hover:text-white transition-colors"
                 target="_blank" rel="noopener noreferrer">
                GitHub
              </a>
              <a href="https://stacksx402.com"
                 className="text-gray-400 hover:text-white transition-colors"
                 target="_blank" rel="noopener noreferrer">
                Main Site
              </a>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-gray-800/50 text-center text-sm text-gray-500">
            © 2024 x402 Stacks. Open source under MIT License.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default GettingStarted;
